import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ApplicationSelection } from '../../shared/models/application.model';
import { IServerSettings } from '../../shared/models/serversettings';
import { ICustomer } from '../models/customer.model';
import { AuthenticationService } from './authentication.service';
import { SettingsService } from './settings.service';

@Injectable()
export class CustomerService {
    private customerServiceUrl: string;
    private serverSettings: IServerSettings;

    private applicationName = 'Not selected';

    private applicationSource = new BehaviorSubject<ApplicationSelection>(new ApplicationSelection(this.applicationName, 'Not selected'));
    applicationChanged$ = this.applicationSource.asObservable();

    private customerSource = new BehaviorSubject<ICustomer>(null);
    customerChanged$ = this.customerSource.asObservable();

    constructor(private http: HttpClient, private settingsService: SettingsService, private authService: AuthenticationService) {
        this.serverSettings = this.settingsService.getSettings();
        this.customerServiceUrl = `${this.serverSettings.CommonApiGatewayUrl}/ids/v1/customer/`;

    }

    getCustomers(paramsContainer): Observable<ICustomer[]> {
      return this.http.post(`${this.customerServiceUrl}GetActiveCustomers`, paramsContainer, { headers: this.getCustomerHeaders() })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

  getCustomerAppVariants(customerId: number, applicationId: number): Promise<any[]> {
    return this.http.post(`${this.customerServiceUrl}GetApplicationVariantsByClientId?clientId=${customerId}&applicationId=${applicationId}`, {}, { headers: this.getCustomerHeaders() })
      .pipe(map(this.extractData),
        catchError(this.handleErrors))
      .toPromise();
  }

  private getCustomerHeaders(): HttpHeaders {
    const authData = this.authService.getAuthData();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'UserId': authData.userGuid,
      'CustomerId': '',
      'ApplicationVariantId': authData.applicationVariantId ? authData.applicationVariantId.toString() : '',
      'Permission': 'SSDP.CanReadCompany',
      'Authorization': this.authService.getBearerToken()
    });
  }

    addCustomer(paramsContainer): Observable<number> {
        const url = `${this.customerServiceUrl}addcustomer`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': this.authService.getBearerToken()
        });
        return this.http.post(url, paramsContainer, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }

    getApplications(): Observable<number> {
        const url = `${this.customerServiceUrl}getapplications`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': this.authService.getBearerToken()
        });
        return this.http.post(url, null, { headers: headers })
            .pipe(map(this.extractData),
              catchError(this.handleErrors));
    }


    private extractData(response: Response) {

        if (response.status < 200 || response.status >= 300) {
            throw new Error('Bad response status: ' + response.status);
        }

        const body = response;

        return body || {};
    }

    public changeSelectedApplication(application: string): void {
        this.applicationName = application;
        this.applicationSource.next(new ApplicationSelection(application, '' /*this.serverSettings.Environment*/));
    }

    public changeSelectedCustomer(customer: ICustomer): void {

        this.customerSource.next(customer);
    }
    private handleErrors(error: any): Observable<any> {
        const errors: string[] = [];

        switch (error.status) {
            case 400: // Bad Request or Model State Error
                const err = error.json();
                if (err.modelState) {
                    const valErrors = error.json().modelState;
                    for (const key in valErrors) {
                        for (let i = 0; i < valErrors[key].length; i++) {
                            errors.push(valErrors[key][i]);
                        }
                    }
                } else if (err.message) {
                    errors.push(err.message);
                } else {
                    errors.push('An Unknown error occurred.');
                }
                break;

            case 404: // Not Found
                errors.push('Resource not found.');
                break;

            case 500: // Internal Error
                errors.push(error.json().exceptionMessage);
                break;

            default:
                errors.push('Status: ' + error.status
                    + ' - Error Message: '
                    + error.statusText);
                break;
      }

        return throwError(errors.join(';')); // Observable.throw(errors);
    }

}
